import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE || '/api';

/**
 * Get reel feed with pagination and interleaved brand reels
 * @param {number} page - Page number
 * @param {string} feed - 'product' or 'lifestyle' (default: 'product')
 */
export const getReelFeed = async (page = 1, feed = 'product') => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/reels/feed`,
      { params: { page, feed } }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch reel feed');
  }
};

/**
 * Get single reel by ID (for shareable page)
 */
export const getReelById = async (reelId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/reels/${reelId}`
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch reel');
  }
};

/**
 * ADMIN: Upload a product reel
 */
export const uploadReel = async (formData, token) => {
  try {
    // debug: log URL being requested (helps when .env doesn't include /api)
    // eslint-disable-next-line no-console
    console.debug('uploadReel POST URL:', `${API_BASE_URL}/admin/reels`);
    const response = await axios.post(
      `${API_BASE_URL}/admin/reels`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to upload reel');
  }
};

/**
 * ADMIN: Get all reels with pagination
 */
export const getAllReels = async (page = 1, limit = 10, token) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/reels`,
      {
        params: { page, limit },
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch reels');
  }
};

/**
 * ADMIN: Delete a reel
 */
export const deleteReel = async (reelId, token) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/admin/reels/${reelId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete reel');
  }
};

/**
 * BRAND: Upload a brand reel
 */
export const uploadBrandReel = async (formData, token) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/reels/brand`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to upload brand reel');
  }
};

/**
 * ADMIN: Upload a lifestyle reel
 */
export const uploadLifestyleReel = async (formData, token) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/reels/lifestyle`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to upload lifestyle reel');
  }
};

/**
 * ADMIN: Update reel metadata (with optional video/preview image replacement)
 */
export const updateReel = async (reelId, data, token) => {
  try {
    // Check if data is FormData (has files) or plain object
    const isFormData = data instanceof FormData;
    
    // For FormData, let axios set the Content-Type automatically (includes boundary)
    const headers = {
      'Authorization': `Bearer ${token}`,
    };
    
    // Only set Content-Type for JSON data; for FormData let axios handle it
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    const response = await axios.put(
      `${API_BASE_URL}/admin/reels/${reelId}`,
      data,
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update reel');
  }
};

export default {
  getReelFeed,
  getReelById,
  uploadReel,
  getAllReels,
  deleteReel,
  uploadBrandReel,
  uploadLifestyleReel,
};
